import { isUnauthenticatedRoutePath } from 'app/router/unauthenticatedRoutePaths';
import axios from 'axios';

/**
 * The structure of a successful api response.
 * @template T The type of the data returned by the operation.
 * */
interface SuccessfulApiResponse<T> {

    /** The data returned by the operation. */
    Data: T;

    /** Whether the operation was successful or not. */
    Successful: true;
}

/**
 * The structure of a failed api response.
 * */
interface FailedApiResponse {

    /** The exception thrown during the operation. */
    Exception: any;

    /** Whether the operation was successful or not. */
    Successful: false;
}

/**
 * The structure of a failed api response.
 * */
export interface PromptNotification {

    /** Successful message returned from the server. */
    message: string;

    /** Whether or not the notification is showing. */
    show: boolean;

    /** If the API call was a success/fail. */
    success: boolean;
}

export interface FullfilledApiResponse {
    Done: boolean;
    ExtraData?: number;
    Id?: number;
    Message: string;
}

/**
 * The structure of a generic api response.
 * @template T The type of the data returned by the operation, if successful.
 * */
export type ApiResponse<T> = SuccessfulApiResponse<T> | FailedApiResponse;

type SingleOrArray<T> = T extends boolean ? boolean | boolean[] : T extends any ? T | T[] : never;

// ---------------------------------------------------------------------------
// CSRF token management
// ---------------------------------------------------------------------------

let cachedCsrfToken: string | null | undefined;

export const setCsrfToken = (token: string | null | undefined): void => {

    cachedCsrfToken = token || null;

};

export const invalidateCsrfCache = (): void => {

    cachedCsrfToken = undefined;

};

// Private axios instance — keeps interceptors isolated from any other axios usage.
// Accept all HTTP status codes so we can unwrap the { Successful, Data, Exception }
// envelope ourselves, matching the previous native-fetch behaviour exactly.
const apiClient = axios.create({
    validateStatus: () => true
});

// Attach the CSRF token header on mutating requests
apiClient.interceptors.request.use(config => {

    const method = (config.method || 'get').toLowerCase();
    if (method === 'get' || method === 'head' || method === 'options') return config;

    if (cachedCsrfToken) {

        config.headers = config.headers || {};
        config.headers['RequestVerificationToken'] = cachedCsrfToken;

    }

    return config;

});

// ---------------------------------------------------------------------------
// Session-expiry handling
// ---------------------------------------------------------------------------

// Guards against firing multiple redirects when several in-flight requests all
// come back 401 at once.
let sessionExpiredRedirectInFlight = false;

const redirectToLoginForExpiredSession = (): void => {

    if (sessionExpiredRedirectInFlight) return;

    // Already on a public/auth route (e.g. the login page itself): nothing to do.
    // This also avoids redirect loops when public endpoints legitimately 401.
    if (isUnauthenticatedRoutePath(window.location.pathname)) return;

    sessionExpiredRedirectInFlight = true;

    // Preserve where the user was so we can send them back after re-authenticating,
    // and flag the expiry so the login page can surface a message.
    const returnTarget = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?ReturnUrl=${returnTarget}&SessionExpired=true`;

};

// When the server reports the request is unauthenticated, the session has
// expired (or was never established). Bounce the user back to the login page.
apiClient.interceptors.response.use(response => {

    if (response.status === 401) {

        redirectToLoginForExpiredSession();

    }

    return response;

});

/**
 * Represents an api caller to an api controller on the server.
 * */
export class ApiCaller {

    /** The name of the api controller to call */
    private controller: string;

    /**
     * Initialize a new ApiCaller.
     * @param controller The api controller to call
     * @returns A new ApiCaller.
     */
    public constructor(controller: string) {

        this.controller = controller;

    }

    /**
     * Handles an api response, throwing a serialized exception if there is one.
     * @template T The data returned by the api operation.
     * @param json The parsed response body from the api controller.
     */
    private handleApiResponse<T>(json: ApiResponse<T>): T {

        if (json.Successful) {

            return json.Data;

        } else {

            throw (json as FailedApiResponse).Exception;

        }

    }

    /**
     * Execute an HTTP GET operation.
     * @template T The data returned by the api operation.
     * @param endpoint The method of the api controller to call. Makes up the endpoint of the url.
     * @param queryParameters Parameters to make up the query string in the URL.
     */
    public async GET<T>(
        endpoint: string,
        queryParameters?: Record<string, SingleOrArray<number | boolean | string> | null>
    ): Promise<T> {

        let url = `/Api/${this.controller}/${endpoint}`;
        if (queryParameters) {

            const parameterStrings: string[] = [];
            for (const key of Object.keys(queryParameters)) {

                const value = queryParameters[key];
                if (value === null) {

                    continue;

                }
                const encodedKey = encodeURIComponent(key);
                if (Array.isArray(value)) {

                    const parameterString = value
                        .map(element => `${encodedKey}=${encodeURIComponent(element)}`)
                        .join('&');
                    parameterStrings.push(parameterString);

                } else {

                    parameterStrings.push(`${encodedKey}=${encodeURIComponent(value)}`);

                }

            }
            if (parameterStrings.length > 0) {

                url += '?' + parameterStrings.join('&');

            }

        }

        const response = await apiClient.get(url);
        return this.handleApiResponse<T>(response.data);

    }

    /**
     * Execute an HTTP POST operation.
     * @template T The data returned by the api operation.
     * @param endpoint The method of the api controller to call. Makes up the endpoint of the url.
     * @param data Data to put in the body of the request as a JSON string.
     */
    public async POST<T>(endpoint: string, data: any): Promise<T> {

        const response = await apiClient.post(`/Api/${this.controller}/${endpoint}`, data);
        return this.handleApiResponse<T>(response.data);

    }

    /**
     * Execute an HTTP PUT operation.
     * @template T The data returned by the api operation.
     * @param endpoint The method of the api controller to call. Makes up the endpoint of the url.
     * @param data Data to put in the body of the request as a JSON string.
     */
    public async PUT<T>(endpoint: string, data: any): Promise<T> {

        const response = await apiClient.put(`/Api/${this.controller}/${endpoint}`, data);
        return this.handleApiResponse<T>(response.data);

    }

    /**
     * Execute an HTTP DELETE operation.
     * @template T The data returned by the api operation.
     * @param endpoint The method of the api controller to call. Makes up the endpoint of the url.
     * @param data Data to put in the body of the request as a JSON string.
     */
    public async DELETE<T>(endpoint: string, data: any): Promise<T> {

        const response = await apiClient.delete(`/Api/${this.controller}/${endpoint}`, { data });
        return this.handleApiResponse<T>(response.data);

    }

}
